import {
  Geofence,
  RouteMilestoneFeature,
  RouteQueryResponse,
} from "./../../../redux/services/types/index";
import LivingMap, {
  FilterKing,
  GlobalFilters,
  LivingMapPlugin,
  LMFeature,
  StateType,
  Utils,
} from "@livingmap/core-mapping";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { lineString } from "@turf/helpers";
import along from "@turf/along";
import length from "@turf/length";
import booleanIntersects from "@turf/boolean-intersects";

import { Feature, MultiLineString, Point } from "geojson";
import mapboxgl from "mapbox-gl";
import { breakpoints } from "../../../hooks/useResponsive";
import { MappedFloor } from "../../../redux/services/types";
import FloorControl from "./floor-control";
import { EMPTY_DATA_SOURCE } from "./position-control";
import { EventTypes, LayerIds, PLUGIN_IDS, SourceIds } from "./types";

const {
  createGeoJSONFeature,
  createGeoJSONFeatureCollection,
  createGeoJSONGeometryPoint,
} = Utils;

interface RoutePointProperties {
  route_icon: string;
  floor_id?: number;
}

interface Coordinate {
  x: number;
  y: number;
  floorId?: number;
}

const ANIMATION_MIN_DURATION = 3500;
const ANIMATION_MAX_DURATION = 6000;
const ANIMATION_MS_PER_COORDINATE = 10;
const ANIMATION_FPS = 60;

const easeInOutCubic = (t: number) => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

class RoutingPlugin extends LivingMapPlugin {
  private floorControl: FloorControl | null = null;
  private floors: MappedFloor[] | null = null;
  private filterInstance: FilterKing;
  private handleFloorChangeCallback: (floor: MappedFloor) => void;
  private handleGeofenceIntersectCallback: (geofences: Geofence[]) => void;
  private animationFrameInstance: number = 0;
  private currentRoute: Feature<MultiLineString, {}> | undefined;
  private journeyMode: boolean = false;
  private geofences: Geofence[];
  private routeFeatures: LMFeature[] = [];

  public constructor(
    id: string,
    LMMap: LivingMap,
    floors: MappedFloor[],
    floorChangeCallback?: (floor: MappedFloor) => void,
    geofenceCallback?: (geofences: Geofence[]) => void,
  ) {
    super(id, LMMap);
    this.filterInstance = LMMap.getFilterKing();
    this.floors = floors;
    this.handleFloorChangeCallback =
      floorChangeCallback?.bind(this) || (() => null);
    this.handleGeofenceIntersectCallback =
      geofenceCallback?.bind(this) || (() => null);
    this.geofences = [];
  }

  public activate(): void {
    this.floorControl = this.LMMap.getPluginById<FloorControl>(
      PLUGIN_IDS.FLOOR,
    );

    this.LMMap.on(EventTypes.FLOOR_CHANGED, this.handleFloorChange);
  }

  public deactivate(): void {
    this.LMMap.removeListener(EventTypes.FLOOR_CHANGED, this.handleFloorChange);
    this.clear();
  }

  private setRouteVisualisation(): void {
    // display the routing line with blue and grey
    this.filterInstance.updateLocalFilter(LayerIds.ROUTE_LINE_OTHER_LAYER, {
      globalExclusions: [GlobalFilters.FLOOR],
    });
    this.filterInstance.updateLocalFilter(
      `${LayerIds.ROUTE_LINE_OTHER_LAYER}-base`,
      { globalExclusions: [GlobalFilters.FLOOR] },
    );
    this.filterInstance.updateLocalFilter(LayerIds.ROUTE_PINS_LAYER, {
      globalExclusions: [GlobalFilters.FLOOR],
    });
  }

  public setGeofences(geofences: Geofence[]): void {
    this.geofences = geofences;
  }

  public renderRoute(
    routeGeoJson: RouteQueryResponse["segments"][number]["routeGeoJson"],
    routeMilestones: RouteMilestoneFeature[],
    totalLength: number,
  ) {
    this.clear();
    const origin = routeGeoJson[0];
    const originCoordinates: Coordinate = {
      x: origin.geometry.coordinates[0][0],
      y: origin.geometry.coordinates[0][1],
      floorId: origin.properties.floorId,
    };

    const bounds = this.parseAndDisplayRoute(
      routeGeoJson,
      routeMilestones,
      originCoordinates,
    );

    // Make sure map has loaded before rendering and animating the route.
    //
    // We do this because if we try animating the route before the map has loaded,
    // we may get a race condition where the animation keeps dirtying the map source data,
    // causing Mapbox to think the map has never loaded (as the map sources never stabilises)
    if (this.LMMap.hasMapLoaded()) {
      this.handleMapDisplay(originCoordinates, bounds);
      this.handleRouteAnimation(routeGeoJson, totalLength);
    } else {
      this.LMMap.on("load", () => {
        this.handleMapDisplay(originCoordinates, bounds);
        this.handleRouteAnimation(routeGeoJson, totalLength);
      });
    }
  }

  public renderRoutePins(features: LMFeature[]): void {
    this.routeFeatures = features;

    const mapInstance = this.LMMap.getMapboxMap();

    const originSource = mapInstance.getSource(
      SourceIds.ROUTE_PINS_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    const currentlySelectedFeature =
      this.LMMap.getFeatureStateDelegate().getFeatureForState(
        StateType.SELECTED,
      );

    const mapboxFeatures: Feature[] = this.routeFeatures.map(
      (lmFeature: LMFeature, index) => {
        const feature = Object.assign(
          { properties: {} },
          lmFeature.getMapboxFeature(),
        );

        const centroid = lmFeature.getCentroid();
        if (!centroid)
          throw new Error(
            `Centroid does not exist on LMFeature for: ${lmFeature.getId()}`,
          );

        feature.geometry = {
          type: "Point",
          coordinates: centroid,
        };

        feature.properties = {
          ...feature.properties,
          pin_type: index === 0 ? "start" : "end",
          selected:
            currentlySelectedFeature !== null &&
            currentlySelectedFeature.getId() === lmFeature.getId()
              ? "active"
              : "inactive",
        };

        return feature;
      },
    );

    originSource.setData({
      type: "FeatureCollection",
      features: mapboxFeatures,
    });
  }

  public rerenderRoutePins(): void {
    if (this.routeFeatures) this.renderRoutePins(this.routeFeatures);
  }

  public clear(): void {
    cancelAnimationFrame(this.animationFrameInstance);
    const mapInstance = this.LMMap.getMapboxMap();
    const iconSources = mapInstance.getSource(
      SourceIds.ROUTE_ICON_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    if (iconSources) iconSources.setData(EMPTY_DATA_SOURCE);

    const routeLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (routeLine) routeLine.setData(EMPTY_DATA_SOURCE);

    const routeOtherLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_OTHER_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    if (routeOtherLine) routeOtherLine.setData(EMPTY_DATA_SOURCE);

    const routeAnimatedLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_ANIMATED_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    if (routeAnimatedLine) {
      routeAnimatedLine.setData(EMPTY_DATA_SOURCE);
    }

    const routePins = mapInstance.getSource(
      SourceIds.ROUTE_PINS_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    if (routePins) {
      routePins.setData(EMPTY_DATA_SOURCE);
    }

    this.routeFeatures = [];
    this.currentRoute = undefined;
    this.handleGeofenceIntersectCallback([]);
  }

  private handleFloorChange = (): void => {
    if (this.floors) {
      const floorIndex = this.floors.findIndex(
        (floor) => this.floorControl?.getActiveFloor()?.id === floor.id,
      )!;

      this.handleFloorChangeCallback(this.floors[floorIndex]);
    }

    this.setRouteVisualisation();
  };

  private generatePoint(point: Coordinate, icon: string): Feature {
    const startPointGeometry = createGeoJSONGeometryPoint([point.x, point.y]);
    const featureProperties: RoutePointProperties = { route_icon: icon };

    if (point.floorId !== null) {
      featureProperties.floor_id = point.floorId;
    }

    const feature = createGeoJSONFeature(featureProperties, startPointGeometry);

    return feature;
  }

  private parseAndDisplayRoute(
    geoJsonData: RouteQueryResponse["segments"][number]["routeGeoJson"],
    routeMilestones: RouteMilestoneFeature[],
    from: Coordinate,
  ): mapboxgl.LngLatBoundsLike {
    const mapInstance = this.LMMap.getMapboxMap();

    const iconSources = mapInstance.getSource(
      SourceIds.ROUTE_ICON_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    const routeLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    const routeOtherLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_OTHER_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    const features: Feature[] = [];
    routeMilestones.forEach((routeMilestone) => {
      const coordinate: Coordinate = {
        x: routeMilestone.geometry.coordinates[0],
        y: routeMilestone.geometry.coordinates[1],
      };

      if (routeMilestone.properties.originId) {
        coordinate.floorId = routeMilestone.properties.originId;
      }

      features.push(
        this.generatePoint(coordinate, routeMilestone.properties.mapIcon),
      );
    });

    const startFeatureCollection = createGeoJSONFeatureCollection(features);
    iconSources.setData(startFeatureCollection);

    const bounds = new mapboxgl.LngLatBounds();

    const startLgnLat = new mapboxgl.LngLat(from.x, from.y);
    bounds.extend(startLgnLat);

    const endLngLat = new mapboxgl.LngLat(from.x, from.y);
    bounds.extend(endLngLat);

    const geoJsonDataWithFloor = geoJsonData.map((feature) => {
      feature.geometry.coordinates.forEach((lngLatPair) => {
        bounds.extend(lngLatPair);
      });

      return {
        ...feature,
        properties: {
          ...feature.properties,
          floor_id: feature.properties.floorId,
        },
      };
    });

    routeLine.setData({
      type: "FeatureCollection",
      features: geoJsonDataWithFloor,
    });
    routeOtherLine.setData({
      type: "FeatureCollection",
      features: geoJsonDataWithFloor,
    });

    // local storage of route for positional related updates
    this.currentRoute = this.generateMultiLineString(geoJsonDataWithFloor);
    const currentGeofences =
      this.calculateGeofenceIntersect(geoJsonDataWithFloor);
    this.handleGeofenceIntersectCallback(currentGeofences);

    const exportBounds: mapboxgl.LngLatBoundsLike = [
      bounds.getSouthWest(),
      bounds.getNorthEast(),
    ];

    return exportBounds;
  }

  private calculateGeofenceIntersect(geoJsonData: any): Geofence[] {
    const geofences: Geofence[] = [];
    if (geoJsonData) {
      geoJsonData.forEach((feature: any) => {
        const intersects = this.geofences.filter((geofence) => {
          if (geofence.floor_id === feature.properties.floor_id) {
            return booleanIntersects(geofence.geometry, feature.geometry);
          }
          return false;
        });
        if (intersects.length > 0) {
          intersects.forEach((geofence) => geofences.push(geofence));
        }
      });
    }
    // return distinct geofences only
    return [...new Map(geofences.map((item) => [item["uuid"], item])).values()];
  }

  private generateMultiLineString(
    geoJsonData: any,
  ): Feature<MultiLineString, {}> | undefined {
    if (geoJsonData) {
      return {
        type: "Feature",
        properties: {},
        geometry: {
          type: "MultiLineString",
          coordinates: geoJsonData.map((feature: any) => {
            return feature.geometry.coordinates;
          }),
        },
      };
    }
    return;
  }

  public isRouting() {
    return this.currentRoute && this.journeyMode ? true : false;
  }

  public setJourneyMode(state: boolean) {
    this.journeyMode = state;
  }

  public getNearestRoutePosition(point: Point) {
    if (!this.currentRoute) return;
    return nearestPointOnLine(this.currentRoute, point.coordinates);
  }

  public handleMapRouteMilestoneDisplay(feature: RouteMilestoneFeature): void {
    const coords = {
      x: feature.geometry.coordinates[0],
      y: feature.geometry.coordinates[1],
      floorId: feature.properties.floorId,
    };

    this.handleMapDisplay(coords);
  }

  private handleRouteAnimation(
    geoJsonData: RouteQueryResponse["segments"][number]["routeGeoJson"],
    totalLength: number,
  ) {
    const mapInstance = this.LMMap.getMapboxMap();
    const animatedRouteLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_ANIMATED_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (!animatedRouteLine) return;

    const featureCoordinates: [number, number][] = [];
    geoJsonData.forEach((feature) =>
      featureCoordinates.push(...feature.geometry.coordinates),
    );

    // Excluding the last point to avoid reversed steps at the end
    const route = lineString(featureCoordinates.slice(0, -1));
    // Calculate the total length of the route
    const routeLength = length(route, { units: "meters" });
    // Determine spacing between points along the route so that spacing depends on the route length, not a fixed step
    // Short routes: at least 0.1 meters spacing to avoid too many points
    const spacing = Math.max(totalLength, 0.1);

    const spacedCoordinates: number[][] = [];

    for (let dist = 0; dist < routeLength; dist += spacing) {
      spacedCoordinates.push(
        // Get coordinates at the current distance along the route
        along(route, dist, { units: "meters" }).geometry.coordinates,
      );
    }

    const coordinateCount = spacedCoordinates.length;

    // Determine total animation duration based on route length
    // - Short routes: at least ANIMATION_MIN_DURATION ms
    // - Long routes: capped at ANIMATION_MAX_DURATION ms
    // - Scaling factor: ANIMATION_MS_PER_COORDINATE
    const durationMs = Math.max(
      ANIMATION_MIN_DURATION,
      Math.min(
        ANIMATION_MAX_DURATION,
        coordinateCount * ANIMATION_MS_PER_COORDINATE,
      ),
    );

    // Calculate how many frames to run, based on FPS
    const totalFrames = Math.ceil(durationMs / (1000 / ANIMATION_FPS));

    let currentFrame = 0;

    // Animation function that runs once per frame
    const animateRoute = () => {
      if (currentFrame <= totalFrames) {
        // Calculate how far along the animation is (0 to 1)
        const percent = currentFrame / totalFrames;
        // Apply easing to make animation feel smooth
        const easedProgress = easeInOutCubic(percent);
        // Determine the current position along the route based on progress
        const currentIndex = Math.floor(easedProgress * coordinateCount);
        // Set the length of the visible "tail" (20% of the route)
        const windowSize = Math.floor(coordinateCount / 5);

        // Calculate start of the visible window, ensuring it doesn't go below 0
        const startIndex = Math.max(0, currentIndex - windowSize);

        // Get the subset of coordinates to render (the visible animated segment)
        const visibleCoords = spacedCoordinates.slice(startIndex, currentIndex);

        const feature = createGeoJSONFeature(
          {},
          {
            type: "LineString",
            coordinates: visibleCoords,
          },
        );

        animatedRouteLine.setData(createGeoJSONFeatureCollection([feature]));
        currentFrame++;
      } else {
        // Animation will be complete here so we reset state
        currentFrame = 0;
        animatedRouteLine.setData(createGeoJSONFeatureCollection([]));
      }

      this.animationFrameInstance = requestAnimationFrame(animateRoute);
    };

    // Cancel any previous animation if it exists
    if (this.animationFrameInstance) {
      cancelAnimationFrame(this.animationFrameInstance);
    }

    animateRoute();
  }

  public handleMapDisplay(
    fromCoordinate: Coordinate,
    bounds?: mapboxgl.LngLatBoundsLike,
  ): void {
    if (this.floorControl) {
      const activeFloorId = this.floorControl.getActiveFloor()!.id;

      if (fromCoordinate.floorId !== activeFloorId && this.floors) {
        const floorIndex = this.floors.findIndex(
          (floor) => floor.id === fromCoordinate.floorId,
        )!;
        this.floorControl.setActiveFloor(this.floors[floorIndex]);
      }
    }

    const mapInstance = this.LMMap.getMapboxMap();
    const bearing = mapInstance.getBearing();

    const isMobile = window.innerWidth <= breakpoints.Mobile;

    const padding = {
      top: isMobile ? 80 : 20,
      right: 20,
      bottom: isMobile ? 280 : 70,
      left: isMobile ? 20 : 400,
    };

    if (bounds) {
      mapInstance.fitBounds(bounds, {
        bearing,
        padding,
      });
    } else {
      mapInstance.easeTo({
        center: { lat: fromCoordinate.y, lng: fromCoordinate.x },
        zoom: 18,
        bearing,
      });
    }

    this.LMMap.emit(EventTypes.ROUTE_DISPLAYED);
  }
}

export default RoutingPlugin;
